import React from "react";

import type { HealthStateValue } from "~/models/dto";
import { fps } from "~/tools/fps";
import { max } from "~/tools/max";
import { min } from "~/tools/min";
import { type GetCssVar, useCssVars } from "~/tools/use-css-vars/index.js";
import { useThrottle } from "~/tools/use-throttle";

import { healthStateToColorToken } from "../../common/functions";
import type {
	Api,
	ComponentHealthChange,
	MonitorCheckStatusHealthChange,
	TimelineSummaryHealthChange,
} from "../../common/openapi";
import { Group, type GroupProps, Line, Rect, useContainerSize } from "../_canvas";

import { useTimelineContext, useTimelineRange, useUpdateFrozenTimeRangeOnChange } from "./_context";
import { type TimelineHoveredLine, TimelinePopover } from "./_TimelinePopover";
import { getHealthSegments } from "./health";

const LOADING_LINE_OPACITY = 0.2;
const HEALTH_LINE_THICKNESS_PX = 2;

export interface TimelineHealthStateProps extends Pick<GroupProps, "x" | "y"> {
	health?:
		| ReadonlyArray<TimelineSummaryHealthChange>
		| ReadonlyArray<MonitorCheckStatusHealthChange>
		| ReadonlyArray<ComponentHealthChange>;
	popoverContent?: React.ReactNode;
	popoverId?: string;
	animate?: boolean;
}

export const TimelineHealthState: React.FC<TimelineHealthStateProps> = ({
	health,
	x,
	y,
	popoverContent,
	popoverId,
	animate,
}) => {
	const [setElement, getCssVar] = useCssVars<HTMLDivElement>();
	const { width } = useContainerSize();
	const [timelineStart, timelineEnd] = useTimelineRange();

	const lineColor = getCssVar("--dataviz-fill-health-timeline-none");

	const healthSegments = React.useMemo(() => {
		if (health == null) {
			return [];
		}

		return getHealthSegments({
			healthChanges: health,
			timelineWidth: width,
			timelineStart,
			timelineEnd,
			minWidth: 4,
			gap: 2,
		});
	}, [health, width, timelineStart, timelineEnd]);

	useUpdateFrozenTimeRangeOnChange([health]);

	return (
		<div className="contents" ref={setElement}>
			<Group x={x} y={y}>
				{health == null && (
					<Line
						opacity={LOADING_LINE_OPACITY}
						points={[
							{ x: 0, y: 0 },
							{ x: width, y: 0 },
						]}
						stroke={lineColor}
						strokeWidth={HEALTH_LINE_THICKNESS_PX}
					/>
				)}

				{healthSegments.map(({ health, leftPx, rightPx }) => {
					if (leftPx >= rightPx) {
						return null;
					}

					return (
						<HealthLine
							animate={animate}
							end={min(width, rightPx)}
							getCssVar={getCssVar}
							healthState={health}
							key={leftPx}
							start={max(0, leftPx)}
						/>
					);
				})}

				{/* ensure the Popover component is rendered after other elements to maintain the highest z-index and prevent it from being overlapped by other elements */}
				{popoverContent && popoverId && (
					<LockedLinePopover hoverAreaWidth={width} popoverContent={popoverContent} popoverId={popoverId} />
				)}
			</Group>
		</div>
	);
};

const HealthLine: React.FC<{
	start: number;
	end: number;
	healthState: Api.HealthStateValue | HealthStateValue | undefined;
	animate?: boolean;
	getCssVar: GetCssVar;
}> = ({ start, end, healthState, animate, getCssVar }) => (
	<Line
		animate={animate && healthState === undefined ? "pulse" : undefined}
		points={[
			{ x: start, y: 0 },
			{ x: end, y: 0 },
		]}
		stroke={getCssVar(healthStateToColorToken(healthState))}
		strokeWidth={HEALTH_LINE_THICKNESS_PX}
	/>
);

const HOVER_AREA_HEIGHT = 14;

const LockedLinePopover: React.FC<{
	hoverAreaWidth: number;
	popoverContent: React.ReactNode;
	popoverId: string;
}> = ({ hoverAreaWidth, popoverContent, popoverId }) => {
	const { lineState } = useTimelineContext();

	const setHoveredLineState = useThrottle((state: TimelineHoveredLine | undefined) => {
		lineState.hoveredLine.write(state);
	}, fps(60));

	return (
		<>
			<TimelinePopover offset={{ x: 10, y: 20 }} popoverId={popoverId}>
				{popoverContent}
			</TimelinePopover>

			{/* a hit area for mouse hover effects */}
			<Rect
				height={HOVER_AREA_HEIGHT}
				onMouseLeave={() => {
					setHoveredLineState(undefined);
				}}
				onMouseMove={({ evt: event, target }) => {
					if (event.target instanceof HTMLCanvasElement) {
						const canvasRect = event.target.getBoundingClientRect();
						const { x, y } = target.getAbsolutePosition();

						setHoveredLineState({
							position: { x: event.x + x, y: canvasRect.y + y },
							popoverId,
						});
					}
				}}
				width={hoverAreaWidth}
				y={-HOVER_AREA_HEIGHT / 2}
			/>
		</>
	);
};
